/**
 * Sync Position Data Handler - 同步持仓数据处理器
 *
 * 负责处理 syncPositionData API 的完整逻辑
 */

import log from "logbeacon";
import type { OperationResult } from "../types";
import type { PositionsAccountService } from "../PositionsAccountService";
import type { SyncPositionDataRequest } from "@/src/features/api/memberApi";

export class SyncPositionDataHandler {
  constructor(
    private positionsAccountService: PositionsAccountService | null,
    private refreshUserFolders: () => Promise<void>,
    private handleError: (error: Error) => void,
  ) {}

  /**
   * Execute sync position data
   * @param accountId 账户ID
   * @param data 同步请求数据
   * @returns Promise<OperationResult> 操作结果
   */
  async execute(
    accountId: string,
    data?: SyncPositionDataRequest,
  ): Promise<OperationResult> {
    if (!this.positionsAccountService || !accountId) {
      log.warn(
        "[SyncPositionDataHandler] PositionsAccountService not initialized or accountId is empty",
      );
      return {
        success: false,
        error: "Service not initialized or accountId is empty",
      };
    }

    try {
      // 调用 API 同步数据
      const result = await this.positionsAccountService.syncPositionData(
        accountId,
        data,
      );

      if (result.success) {
        // 同步成功后，强制刷新 userFolders 缓存以获取最新的 asset_count
        await this.refreshUserFolders();
        log.debug(
          `[SyncPositionDataHandler] Successfully synced position data for account: ${accountId}`,
          {
            accountId,
            syncData: result.data,
          },
        );
      }

      return {
        success: result.success,
        error: result.success ? undefined : result.message,
        message: result.message,
        data: result.data,
        // 保留原始错误对象信息（如果存在）
        errorDetails: result.error,
      };
    } catch (error) {
      log.error(
        "[SyncPositionDataHandler] Failed to sync position data:",
        error,
      );
      this.handleError(error as Error);
      return {
        success: false,
        error: (error as Error).message || "Unknown error",
      };
    }
  }
}
